import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  PLATFORM_ID
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  UntypedFormBuilder,
  NG_VALUE_ACCESSOR,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MonoFieldValueAccessorComponent } from '../mono-field-value-accessor.component';
import {
  Options,
  PointerType,
  NgxSliderModule
} from '@angular-slider/ngx-slider';
import type { IRangeOptions } from './range-options.interface';
import { ChangeContext } from '@angular-slider/ngx-slider/change-context';
import { isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SdFormInputComponent } from '../form-input/form-input.component';
import { ErrorTranslateService } from '../error-translate-service';
import { BaseFormErrorTranslateService } from '../form-common-error/base-form-error-translate.service';

@Component({
  selector: 'sd-form-range',
  templateUrl: './form-range.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SdFormRangeComponent),
      multi: true
    },
    { provide: BaseFormErrorTranslateService, useClass: ErrorTranslateService }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SdFormInputComponent,
    NgxSliderModule,
    TranslateModule
  ]
})
export class SdFormRangeComponent
  extends MonoFieldValueAccessorComponent<number>
  implements ControlValueAccessor, OnInit
{
  constructor(
    cdRef: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    @Inject(PLATFORM_ID) private platformId: any,
    @Optional() private controlContainer: ControlContainer,
  ) {
    super(cdRef);
  }

  formRangeGroup: any;
  _rangeOptions: IRangeOptions;
  delay = 500;
  pointerType = PointerType;
  showSlider = true;

  @Input()
  inputMinValue: number;

  @Input()
  inputMaxValue: number;

  @Input()
  get rangeOptions() {
    return this._rangeOptions;
  }
  set rangeOptions(rangeOptions: IRangeOptions) {
    this._rangeOptions = rangeOptions;
  }

  @Input()
  options: Options;

  @Input()
  displayInput = true;

  @HostBinding('class.has-hidden-limit')
  @Input()
  hideLimit = true;

  @Output()
  rangeValue = new EventEmitter<void>();

  sliderOption(): Options {
    return {
      floor: this._rangeOptions?.minValue,
      ceil: this._rangeOptions?.maxValue,
      step: this._rangeOptions?.step,
      enforceStep: false,
      enforceRange: false,
      noSwitching: true
    };
  }

  ngOnInit() {
    this.showSlider = isPlatformBrowser(this.platformId);
    this.formRangeGroup =
      this.controlContainer?.control ||
      this.fb.group({
        minSelected: this._rangeOptions?.minValue,
        maxSelected: this._rangeOptions?.maxValue
      });

    this.inputMinValue = this._rangeOptions?.minValue;
    this.inputMaxValue = this._rangeOptions?.maxValue;
  }

  valueChange(event: ChangeContext) {
    let newMin = event?.value ?? this.formRangeGroup.get('minSelected')?.value;
    let newMax =
      event?.highValue ?? this.formRangeGroup.get('maxSelected')?.value;

    newMin = newMin <= 0 ? this._rangeOptions?.minValue : newMin;

    if (Math.round(newMin) > Math.round(newMax)) {
      if (event.pointerType === PointerType.Min) {
        newMin = newMax;
      } else {
        newMax = newMin;
      }
    }

    this.formRangeGroup.patchValue({
      minSelected: Number(newMin),
      maxSelected: Number(newMax)
    });
    this.inputMinValue = this.formRangeGroup.get('minSelected').value;
    this.inputMaxValue = this.formRangeGroup.get('maxSelected').value;
    this.rangeValue.emit();
  }

  mappingChildToParent(value: number): number {
    return value;
  }

  mappingParentToChild(value: number): number {
    return value;
  }
}
